import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Radio, Send, Loader2, ShieldAlert, AlertTriangle, Sparkles } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_OPTIONS = [
  { value: 'info',     label: 'Info',     Icon: Sparkles,      color: 'text-[#002FA7]' },
  { value: 'warning',  label: 'Warning',  Icon: AlertTriangle, color: 'text-[#FF9500]' },
  { value: 'critical', label: 'Critical', Icon: ShieldAlert,   color: 'text-[#FF3B30]' },
];

/**
 * Admin card that triggers a system-wide update push. Rotates the server-side
 * `build_id` so every connected client (on its next 60-second poll or next
 * focus event) sees a mismatch and shows a "Update now" toast with a one-click
 * hard-refresh button.
 *
 * Use cases:
 * - Post-deploy ("We just pushed a fix — please reload").
 * - Security incident ("Critical patch — reload immediately").
 * - Coordinating big feature rollouts.
 */
const PushUpdateCard = ({ token }) => {
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('info');
  const [sending, setSending] = useState(false);
  const [version, setVersion] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadVersion = async () => {
    try {
      const r = await axios.get(`${API}/system/version`);
      setVersion(r.data);
    } catch { /* silent */ }
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/system/broadcasts`, { headers, params: { limit: 10 } });
      setHistory(r.data.broadcasts || []);
    } catch { /* silent */ } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { loadVersion(); loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBroadcast = async () => {
    if (!window.confirm(
      'This will show an "Update now" toast to every connected user within 60 seconds. ' +
      'Proceed?'
    )) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/system/broadcast-update`, {
        message: message.trim(),
        severity,
      }, { headers });
      toast.success(`Broadcast sent — new build ${r.data.build_id.slice(0, 8)}…`);
      setMessage('');
      setSeverity('info');
      loadVersion();
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to broadcast update');
    } finally { setSending(false); }
  };

  return (
    <Card className="border-[#E5E7EB]" data-testid="push-update-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Radio className="w-4 h-4 text-[#002FA7]" />
          Push Update to All Users
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[11px] text-[#4B5563] leading-relaxed">
          Force every connected browser to show an <strong>Update now</strong> toast. Clients poll every
          60s and on tab-focus, so the banner appears within a minute. Use this after a deployment,
          for critical security patches, or to coordinate a feature rollout.
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-[11px] text-[#4B5563]">Message (optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Security patch — please reload to apply. (Leave blank for default copy.)"
              rows={2}
              maxLength={500}
              className="text-xs resize-none"
              data-testid="push-update-message"
            />
            <p className="text-[10px] text-[#9CA3AF]">{message.length}/500</p>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-[#4B5563]">Severity</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="text-xs h-9" data-testid="push-update-severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEVERITY_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>
                    <div className="flex items-center gap-2">
                      <o.Icon className={`w-3.5 h-3.5 ${o.color}`} />
                      {o.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[#9CA3AF]">
              Critical toasts cannot be dismissed.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-[#F3F4F6]">
          <div className="text-[11px] text-[#4B5563]">
            Current build:&nbsp;
            <code className="bg-[#F3F4F6] px-1.5 py-0.5 rounded font-mono text-[10px]" data-testid="push-update-current-build">
              {version?.build_id || '—'}
            </code>
            <span className="mx-2 text-[#E5E7EB]">|</span>
            v{version?.version || '—'}
          </div>
          <Button
            onClick={handleBroadcast}
            disabled={sending}
            className="bg-[#002FA7] hover:bg-[#00227A] h-8 text-xs"
            data-testid="push-update-broadcast-btn"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Broadcast update
          </Button>
        </div>

        {/* Broadcast history */}
        <div className="pt-2 border-t border-[#F3F4F6]">
          <p className="text-[11px] font-semibold text-[#4B5563] mb-2">Recent broadcasts</p>
          {loadingHistory && (
            <p className="text-[11px] text-[#9CA3AF]"><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Loading…</p>
          )}
          {!loadingHistory && history.length === 0 && (
            <p className="text-[11px] text-[#9CA3AF]">No broadcasts yet.</p>
          )}
          {!loadingHistory && history.length > 0 && (
            <div className="space-y-1.5" data-testid="push-update-history">
              {history.map((b) => {
                const meta = SEVERITY_OPTIONS.find(o => o.value === b.severity) || SEVERITY_OPTIONS[0];
                return (
                  <div
                    key={b.broadcast_id}
                    className="flex items-start gap-2 p-2 bg-[#FAFAFA] rounded text-[11px]"
                  >
                    <meta.Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant="outline" className="text-[9px] px-1 py-0 h-[14px]">
                          {meta.label}
                        </Badge>
                        <span className="text-[#4B5563]">{b.issued_by}</span>
                        <span className="text-[#9CA3AF]">
                          {b.issued_at ? formatDistanceToNow(new Date(b.issued_at), { addSuffix: true }) : ''}
                        </span>
                      </div>
                      {b.message && (
                        <p className="text-[#0A0A0A] mt-0.5 line-clamp-2">{b.message}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PushUpdateCard;
